<template>
  <v-dialog
    hide-overlay
    :overlay-opacity="0"
    :transition="false"
    persistent
    ref="dialog"
    width="561px"
    :value="!!isOpen"
    absolute
  >
    <div class="card-overlay" @click="onOverlayClick"/>

    <v-card style="width: 561px" v-if="isOpen">
      <template v-if="selectedTheme !== undefined && Object.keys(selectedTheme).length && selectedTheme.screenshot_url">
        <v-img
          height="285px"
          width="100%"
          position="top center"
          :src="selectedTheme.screenshot_url"
        />
        <v-divider/>
      </template>
      <v-card-text class="px-10 pt-8">
        <v-btn
          v-if="!loading"
          :disabled="loading"
          x-small
          icon
          @click="closeModal"
          class="close-model"
          :class="{ 'btn-loading-block': loading }"
        >
          <v-icon>$close</v-icon>
        </v-btn>
        <h6>{{ $t("heading.onboarding.chooseProduct.domain.title") }}</h6>
        <p class="p-3 mt-2">
          {{ $t("heading.onboarding.chooseProduct.domain.subtitle") }}
        </p>
        <v-form ref="domainForm" @submit.prevent="onEnter">
          <v-combobox
            ref="domainCombobox"
            outlined
            :placeholder="$t('form.placeholder.domain')"
            v-model="domain"
            :search-input.sync="domainSearch"
            :items="domainComboboxItems"
            item-text="domain"
            return-object
            hide-no-data
            hide-details="auto"
            :filter="passthroughDomainFilter"
            :rules="domainRulesWithBlacklist"
            :error-messages="domainValidationError ? [domainValidationError] : []"
            class="v-input--lg"
            large
            validate-on-blur
            :disabled="loading"
            :loading="domainsLoading"
            :menu-props="{
              offsetY: true,
              nudgeBottom: '8px',
              closeOnContentClick: true,
              contentClass:
                'custom-dropdown-select custom-dropdown-select--onboarding',
            }"
            @change="onDomainComboboxChange"
            @focus="onDomainComboboxFocus"
            @click="onDomainComboboxClick"
            @blur="onDomainBlur"
            @input="onDomainInput"
            @keydown.enter.prevent="onEnter"
          />
        </v-form>
      </v-card-text>
      <v-card-actions class="d-flex flex-column pb-4 px-10 pt-2">
        <v-btn
          x-large
          elevation="0"
          @click="selectDomain()"
          :loading="loading || domainValidationLoading"
          :disabled="isContinueButtonDisabled"
          color="primary"
          block
        >
          <template v-slot:loader>
            <v-progress-circular
              indeterminate
              size="20"
              width="2"
              class="mr-2"
            ></v-progress-circular>
            <span v-if="loadingText !== ''">{{ $t(loadingText) }}</span>
            <span v-else>{{ $t('button.onboarding.check-domain-availability') }}</span>
          </template>
          <template
            v-if="
              isSelectedServiceServerAllowTransferDomain()
              || hasSelectedServiceInstantVerificationEnabled()
              || installationType !== 'wordpress'
            ">
            <span>{{ $t("button.continue") }}</span>
            <v-icon size="24" class="ml-2">$arrowright</v-icon>
          </template>
          <template v-else-if="hasSelectedServiceAskForSiteName()">
            <span>{{ $t("button.website.set") }}</span>
            <v-icon size="24" class="ml-2">$arrowright</v-icon>
          </template>
          <template v-else>
            <span>{{ $t("button.website.create") }}</span>
            <v-icon size="24" class="ml-2">$arrowright</v-icon>
          </template>
        </v-btn>
        <v-btn
          v-if="services.length > 1"
          :disabled="loading"
          @click="$emit('change-service-button-clicked')"
          x-large
          elevation="0"
          class="mx-0 mt-2"
          color="gray"
          text
          block
        >
          <span class="p-1 font-weight-light gray--text text--darken-1">
            {{ $t("button.service.change") }}
          </span>
        </v-btn>
        <v-btn
          v-else-if="canSelectedServiceChooseTheme() && installationType === 'wordpress'"
          :disabled="loading"
          @click="$emit('change-theme-button-clicked')"
          x-large
          elevation="0"
          class="mx-0 mt-2"
          color="gray"
          text
          block
        >
          <span class="p-2 font-weight-light gray--text text--darken-1">
            {{ $t("button.theme.change") }}
          </span>
        </v-btn>
        <v-btn
          v-else
          :disabled="loading"
          @click="closeModal"
          x-large
          elevation="0"
          class="mx-0 mt-2"
          color="gray"
          text
          block
        >
          <span class="p-2 font-weight-light gray--text text--darken-1">
            {{ $t("button.maybeLater") }}
          </span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Api from "../../apis/Api";
import _ from "lodash";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import OnboardingSelectedServiceMixin from "../../mixins/Onboarding/SelectedServiceMixin";
import ServiceDomainsMixin from "../../mixins/Onboarding/ServiceDomainsMixin";

export default {
  mixins: [FormRulesMixin, OnboardingSelectedServiceMixin, ServiceDomainsMixin],
  data: function () {
    return {
      domain: "",
      domainValidationLoading: false,
      domainValidationError: null,
      domainValidationPassed: false,
      domainValidationSeq: 0,
      lastValidatedDomain: null,
      showDomainPickerMenu: false,
    };
  },
  props: {
    isOpen: [Boolean, String],
    loading: Boolean,
    loadingText: {
      type: String,
      default: "",
    },
    selectedTheme: Object,
    services: Array,
    selectedService: Object,
    installationType: { type: String, default: "wordpress" },
  },
  created() {
    this._debouncedValidateDomain = _.debounce(() => {
      if (!this.isDomainFormatValid() || this.isCurrentDomainExisting) {
        return;
      }
      this.validateDomainAvailability();
    }, 500);
  },
  computed: {
    currentDomainName() {
      const fromSearch = (this.domainSearch || "").trim();

      if (typeof this.domain === "object" && this.domain !== null) {
        const fromModel = this.getDomainName(this.domain);
        if (fromSearch && fromSearch.toLowerCase() !== fromModel.toLowerCase()) {
          return fromSearch;
        }
        return fromModel;
      }

      if (fromSearch) {
        return fromSearch;
      }

      return this.getDomainName(this.domain);
    },
    selectedExistingDomain() {
      if (!this.currentDomainName) {
        return null;
      }

      return this.findExistingDomain(this.currentDomainName);
    },
    isCurrentDomainExisting() {
      return !!this.selectedExistingDomain;
    },
    isContinueButtonDisabled() {
      if (this.loading || this.domainValidationLoading) {
        return true;
      }

      if (this.isCurrentDomainExisting) {
        return false;
      }

      return (
        !!this.domainValidationError
        || !this.domainValidationPassed
        || this.lastValidatedDomain !== this.currentDomainName
      );
    },
    domainRulesWithBlacklist() {
      const blacklist = this.selectedService?.onboarding?.domains_blacklist || [];

      const blacklistRule = (value) => {
        if (!value) return true;

        const domain = this.getDomainName(value).toLowerCase();
        const isBlacklisted = blacklist.some(blacklistedDomain => {
          const escapedDomain = blacklistedDomain
            .toLowerCase()
            .replace(/\./g, "\\.")
            .replace(/\*/g, ".*");

          const blacklistRegex = new RegExp(`^${escapedDomain}$|\\.(${escapedDomain})$`);

          return blacklistRegex.test(domain);
        });

        return !isBlacklisted || this.$t("form.validation.domain.blacklisted");
      };

      const normalizeRules = this.domainRulesTld.map(
        (rule) => (value) => rule(this.getDomainName(value))
      );

      return [...normalizeRules, blacklistRule];
    },
    domainComboboxItems() {
      return this.showDomainPickerMenu ? this.serviceDomains : [];
    },
  },
  methods: {
    isServiceDomainsSearchActive() {
      return !!this.isOpen;
    },
    shouldSearchServiceDomainsOnInput() {
      return false;
    },
    resetDomainValidation() {
      this.cancelDebouncedDomainValidation();
      this.domainValidationLoading = false;
      this.domainValidationError = null;
      this.domainValidationPassed = false;
      this.domainValidationSeq = 0;
      this.lastValidatedDomain = null;
    },
    cancelDebouncedDomainValidation() {
      if (
        this._debouncedValidateDomain
        && typeof this._debouncedValidateDomain.cancel === "function"
      ) {
        this._debouncedValidateDomain.cancel();
      }
    },
    flushDebouncedDomainValidation() {
      if (
        this._debouncedValidateDomain
        && typeof this._debouncedValidateDomain.flush === "function"
      ) {
        this._debouncedValidateDomain.flush();
      }
    },
    scheduleDebouncedDomainValidation() {
      if (this._debouncedValidateDomain) {
        this._debouncedValidateDomain();
      }
    },
    markExistingDomainAsValid(domainName) {
      this.cancelDebouncedDomainValidation();
      this.domainValidationLoading = false;
      this.domainValidationError = null;
      this.domainValidationPassed = true;
      this.lastValidatedDomain = domainName;
    },
    resetForm() {
      this.domain = "";
      this.closeDomainPickerMenu();
      this.resetServiceDomains();
      this.resetDomainValidation();
    },
    closeModal() {
      if (!this.loading) {
        this.$emit("remove-modal");
      }
    },
    onOverlayClick() {
      if (!this.loading && this.$refs.dialog) {
        this.$refs.dialog.animateClick();
      }
    },
    openDomainPickerMenu() {
      this.showDomainPickerMenu = true;
      this.$nextTick(() => {
        const combobox = this.$refs.domainCombobox;
        if (combobox) {
          combobox.isMenuActive = true;
        }
      });
    },
    closeDomainPickerMenu() {
      this.showDomainPickerMenu = false;
      const combobox = this.$refs.domainCombobox;
      if (combobox) {
        combobox.isMenuActive = false;
      }
    },
    onDomainComboboxFocus() {
      this.openDomainPickerMenu();
    },
    onDomainComboboxClick() {
      this.openDomainPickerMenu();
    },
    onDomainComboboxChange() {
      const { domainName, existingDomain, modelValue } =
        this.applyDomainComboboxSelection(this.domain);

      this.domain = modelValue;
      this.closeDomainPickerMenu();

      if (existingDomain) {
        this.markExistingDomainAsValid(domainName);
      } else {
        this.onDomainInput();
      }
    },
    onEnter() {
      this.cancelDebouncedDomainValidation();
      this.$nextTick(() => {
        this.selectDomain();
      });
    },
    syncDomainModelFromSearch() {
      const fromSearch = (this.domainSearch || "").trim();

      if (typeof this.domain === "object" && this.domain !== null) {
        const fromModel = this.getDomainName(this.domain);
        if (!fromSearch || fromSearch.toLowerCase() === fromModel.toLowerCase()) {
          return;
        }
      }

      if (!fromSearch) {
        if (typeof this.domain !== "object") {
          this.domain = "";
        }
        return;
      }

      const existingDomain = this.findExistingDomain(fromSearch);
      this.domain = existingDomain || fromSearch;
    },
    onDomainInput() {
      this.syncDomainModelFromSearch();

      const domainName = this.currentDomainName;

      this.domainValidationPassed = false;
      this.domainValidationError = null;
      this.lastValidatedDomain = null;

      if (!domainName) {
        this.cancelDebouncedDomainValidation();
        return;
      }

      if (this.findExistingDomain(domainName)) {
        this.markExistingDomainAsValid(domainName);
        return;
      }

      this.scheduleDebouncedDomainValidation();
    },
    onDomainBlur() {
      this.closeDomainPickerMenu();

      if (!this.currentDomainName || this.isCurrentDomainExisting) {
        return;
      }

      this.flushDebouncedDomainValidation();
    },
    isDomainFormatValid() {
      return this.domainRulesWithBlacklist.every(
        (rule) => rule(this.currentDomainName) === true
      );
    },
    getDomainValidationError(error) {
      const domainErrors = error?.response?.data?.errors?.domain;
      if (Array.isArray(domainErrors) && domainErrors.length > 0) {
        return domainErrors[0];
      }

      return Api.getErrorMessage(error).replace(/^domain:\s*/i, "");
    },
    async validateDomainAvailability() {
      const domainName = this.currentDomainName;

      if (!this.selectedService?.id || !domainName) {
        return false;
      }

      if (this.findExistingDomain(domainName)) {
        this.markExistingDomainAsValid(domainName);
        return true;
      }

      const seq = ++this.domainValidationSeq;
      this.domainValidationLoading = true;
      this.domainValidationError = null;
      this.domainValidationPassed = false;

      try {
        await Api.get(
          `/services/${this.selectedService.id}/domains/validate?domain=${encodeURIComponent(domainName)}`
        );
        if (seq !== this.domainValidationSeq) {
          return false;
        }
        this.domainValidationPassed = true;
        this.lastValidatedDomain = domainName;
        return true;
      } catch (error) {
        if (seq !== this.domainValidationSeq) {
          return false;
        }
        this.domainValidationError = this.getDomainValidationError(error);
        return false;
      } finally {
        if (seq === this.domainValidationSeq) {
          this.domainValidationLoading = false;
        }
      }
    },
    async prepareDomainField() {
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }

      await this.$nextTick();
      this.syncDomainModelFromSearch();

      const { domainName, existingDomain, modelValue } =
        this.syncTypedDomainFromCombobox("domainCombobox", this.domain);
      const resolvedDomainName = (domainName || this.currentDomainName).trim();

      if (resolvedDomainName) {
        this.domain = modelValue || this.findExistingDomain(resolvedDomainName) || resolvedDomainName;
        this.domainSearch = resolvedDomainName;
      }

      await this.$nextTick();

      return {
        domainName: resolvedDomainName,
        existingDomain:
          existingDomain || this.findExistingDomain(resolvedDomainName),
      };
    },
    async selectDomain() {
      if (this.loading) return;

      const { domainName, existingDomain: localExisting } =
        await this.prepareDomainField();

      if (!this.$refs.domainForm.validate()) {
        return;
      }

      let existingDomain =
        localExisting || this.findExistingDomain(domainName);

      if (existingDomain) {
        this.markExistingDomainAsValid(domainName);
      } else if (
        !this.domainValidationPassed
        || this.lastValidatedDomain !== domainName
      ) {
        const ok = await this.validateDomainAvailability();
        if (!ok) {
          return;
        }
        existingDomain = null;
      }

      this.$emit("domain-selected", {
        domain: domainName,
        existingDomain,
      });
    },
    onSelectedServiceIdChanged() {
      this.resetDomainValidation();
      this.clearServiceDomainsList();
      this.cancelDebouncedDomainFetch();
      this.domainsRequestId++;

      if (!this.isServiceDomainsSearchActive()) {
        return;
      }

      this.$nextTick(() => {
        this.loadServiceDomains("");
      });
    },
  },
  beforeDestroy() {
    this.cancelDebouncedDomainValidation();
  },
  watch: {
    domainSearch() {
      if (!this.isOpen) {
        return;
      }

      this.closeDomainPickerMenu();
      this.onDomainInput();
    },
    isOpen: function (value) {
      if (value) {
        this.$store.dispatch("lockBodyScroll");
        this.resetDomainValidation();
        this.openServiceDomainsPicker();
      } else {
        this.$store.dispatch("unlockBodyScroll");
        this.resetForm();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.v-card::v-deep {
  .v-card__text {
    -webkit-line-clamp: unset !important;
  }

  @media (max-width: 480px) {
    .v-card__text,
    .v-card__actions {
      padding-left: 24px !important;
      padding-right: 24px !important;
    }
  }
}

.v-input::v-deep {
  &.v-select.v-input--lg,
  &.v-combobox.v-input--lg {
    .v-input__control .v-input__slot {
      min-height: 56px;
      max-height: fit-content;
      border-radius: 8px;
    }
  }
}

.v-input::v-deep {
  &.v-input--lg {
    .v-input__control .v-input__slot {
      border-radius: 8px;
    }
  }
}

.v-dialog__content::v-deep {
  .v-dialog {
    overflow-y: auto;
    overflow-x: hidden;
  }
}
</style>

<style lang="scss">
.close-model {
  position: absolute;
  top: -40px;
  right: -8px;
}

.custom-dropdown-select--onboarding::v-deep {
  overflow-y: auto !important;
  padding-left: 16px;

  .v-list-item {
    color: unset !important;
    caret-color: unset !important;
  }
}
</style>
